import {
  Bot,
  Paperclip,
  Plus,
  Send,
  Sparkles,
  Upload,
  User,
  X,
} from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { useChatMessages } from '../hooks/useChatMessages';
import { CONNECTORS, formatFileSize } from '../utils/helpers';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Textarea } from './ui/textarea';
import './ChatInterface.css';

const PLAN_STORAGE_KEY = 'dummyChatSelectedPlan';
const EMAIL_STORAGE_KEY = 'dummyChatSelectedEmail';
const DISABLE_TRAINING_KEY = 'dummyChatDisableTraining';

const Message = ({ message }) => (
  <div
    className={`message ${message.role === 'user' ? 'message-user' : 'message-assistant'}`}
    data-testid={`message-${message.role}`}
  >
    {message.role === 'assistant' && (
      <div className="avatar avatar-assistant">
        <Bot className="avatar-icon" />
      </div>
    )}
    <div
      className={`message-bubble ${message.role === 'user' ? 'bubble-user' : 'bubble-assistant'}`}
      data-testid="message-content"
    >
      {message.attachments && message.attachments.length > 0 && (
        <div className="message-attachments" data-testid="message-attachments">
          {message.attachments.map((attachment) => (
            <div
              key={attachment.id}
              className={`message-attachment ${message.role === 'user' ? 'user-attachment' : 'assistant-attachment'}`}
              data-testid="attachment-item"
            >
              <Paperclip className="attachment-icon" />
              <span className="attachment-name">{attachment.name}</span>
              <span className="attachment-size">({formatFileSize(attachment.size)})</span>
            </div>
          ))}
        </div>
      )}
      <p className="message-text" data-testid="message-text">
        {message.content}
      </p>
      <span className="message-timestamp">
        {message.timestamp.toLocaleTimeString()}
      </span>
    </div>
    {message.role === 'user' && (
      <div className="avatar avatar-user">
        <User className="avatar-icon" />
      </div>
    )}
  </div>
);

const LoadingIndicator = () => (
  <div className="loading-indicator" data-testid="loading-indicator">
    <div className="avatar avatar-assistant">
      <Bot className="avatar-icon" />
    </div>
    <div className="loading-bubble">
      <div className="loading-dots">
        <div className="loading-dot" />
        <div className="loading-dot loading-dot-delay-1" />
        <div className="loading-dot loading-dot-delay-2" />
      </div>
    </div>
  </div>
);

const Toast = ({ toast }) => {
  if (!toast.visible) return null;
  const classMap = { success: 'xhr-toast-success', error: 'xhr-toast-error', warning: 'xhr-toast-warning' };
  const iconMap = { success: '✓', error: '✕', warning: '⚠' };
  return (
    <div className={`xhr-toast ${classMap[toast.type] ?? 'xhr-toast-error'}`} data-testid="xhr-toast">
      <span className="xhr-toast-icon">{iconMap[toast.type] ?? '!'}</span>
      <span className="xhr-toast-message">{toast.message}</span>
    </div>
  );
};

export const ChatInterfaceSingle = () => {
  const [inputValue, setInputValue] = React.useState('');
  const [attachments, setAttachments] = React.useState([]);
  const [isDraggingInput, setIsDraggingInput] = React.useState(false);
  const [showConnectors, setShowConnectors] = React.useState(false);
  const [selectedEmail, setSelectedEmail] = React.useState('');
  const [selectedPlan, setSelectedPlan] = React.useState('');
  const [disableTrainingOnData, setDisableTrainingOnData] = React.useState(false);

  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const dragCounter = useRef(0);
  const toastTimerRef = useRef(null);

  const [xhrAsync, setXhrAsync] = React.useState(true);
  const [xhrMethod, setXhrMethod] = React.useState('POST');
  const [fetchMethod, setFetchMethod] = React.useState('POST');
  const [toast, setToast] = React.useState({ visible: false, message: '', type: 'success' });

  const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];

  const { messages, isLoading, sendMessage } = useChatMessages();

  const syncSettings = async (email, plan, disableTraining) => {
    try {
      const payload = {
        singulr_dummy_test_app: {
          email: email || null,
          plan: plan || null,
          disable_training_on_data: disableTraining,
        },
      };
      const response = await fetch('https://httpbin.org/post', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      console.log('Settings updated:', data);
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const handleEmailChange = async (email) => {
    setSelectedEmail(email);
    try {
      if (email) {
        window.localStorage.setItem(EMAIL_STORAGE_KEY, email);
      } else {
        window.localStorage.removeItem(EMAIL_STORAGE_KEY);
      }
    } catch {}

    let nextPlan = selectedPlan;
    if (!email) {
      nextPlan = '';
      setSelectedPlan('');
      try { window.localStorage.removeItem(PLAN_STORAGE_KEY); } catch {}
    } else if (!selectedPlan) {
      nextPlan = 'free';
      setSelectedPlan('free');
      try { window.localStorage.setItem(PLAN_STORAGE_KEY, 'free'); } catch {}
    }
    await syncSettings(email, nextPlan, disableTrainingOnData);
  };

  const handlePlanChange = async (plan) => {
    if (!selectedEmail) return;
    setSelectedPlan(plan);
    try {
      if (plan) {
        window.localStorage.setItem(PLAN_STORAGE_KEY, plan);
      } else {
        window.localStorage.removeItem(PLAN_STORAGE_KEY);
      }
    } catch {}
    await syncSettings(selectedEmail, plan, disableTrainingOnData);
  };

  const handleDisableTrainingChange = async (value) => {
    setDisableTrainingOnData(value);
    try { window.localStorage.setItem(DISABLE_TRAINING_KEY, JSON.stringify(value)); } catch {}
    await syncSettings(selectedEmail, selectedPlan, value);
  };

  useEffect(() => {
    try {
      const savedEmail = window.localStorage.getItem(EMAIL_STORAGE_KEY);
      const savedPlan = window.localStorage.getItem(PLAN_STORAGE_KEY);
      const initialEmail = savedEmail || '';
      let initialPlan = '';

      if (initialEmail && (savedPlan === 'free' || savedPlan === 'paid' || savedPlan === 'enterprise')) {
        initialPlan = savedPlan;
      } else if (initialEmail && !initialPlan) {
        initialPlan = 'free';
        try { window.localStorage.setItem(PLAN_STORAGE_KEY, 'free'); } catch {}
      }

      setSelectedEmail(initialEmail);
      setSelectedPlan(initialPlan);

      const savedDisableTraining = window.localStorage.getItem(DISABLE_TRAINING_KEY);
      let initialDisableTraining = false;
      if (savedDisableTraining !== null) {
        try { initialDisableTraining = JSON.parse(savedDisableTraining) === true; } catch {}
      }
      setDisableTrainingOnData(initialDisableTraining);
      syncSettings(initialEmail, initialPlan, initialDisableTraining);
      return;
    } catch (error) {
      console.error('Error reading settings from localStorage:', error);
    }
    syncSettings('', '', false);
  }, []);

  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages]);

  const showToast = (message, type = 'success') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ visible: true, message, type });
    toastTimerRef.current = setTimeout(() => {
      setToast((prev) => ({ ...prev, visible: false }));
    }, 10000);
  };

  const makeAttachments = (rawFiles) =>
    Array.from(rawFiles).map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
      file,
    }));

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (!files) return;
    setAttachments((prev) => [...prev, ...makeAttachments(files)]);
    e.target.value = '';
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const fileItems = Array.from(items).filter((item) => item.kind === 'file');
    if (fileItems.length === 0) return;
    const pastedFiles = fileItems.map((item) => item.getAsFile()).filter(Boolean);
    if (pastedFiles.length > 0) {
      e.preventDefault();
      setAttachments((prev) => [...prev, ...makeAttachments(pastedFiles)]);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    setIsDraggingInput(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDraggingInput(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDraggingInput(false);
    const droppedFiles = e.dataTransfer?.files;
    if (droppedFiles && droppedFiles.length > 0) {
      setAttachments((prev) => [...prev, ...makeAttachments(droppedFiles)]);
    }
  };

  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text && attachments.length === 0) return;
    const currentText = inputValue;
    const currentAttachments = [...attachments];
    setInputValue('');
    setAttachments([]);
    await sendMessage(currentText, currentAttachments);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleXhrClick = () => {
    const openNative = XMLHttpRequest.prototype.open.toString().includes('[native code]');
    const sendNative = XMLHttpRequest.prototype.send.toString().includes('[native code]');
    if (!openNative || !sendNative) {
      showToast(
        `⚠ [XHR PROTOTYPE PATCHED] XMLHttpRequest.prototype.${!openNative ? 'open' : 'send'} has been overridden by an extension or injected script.`,
        'warning'
      );
    }
    const methodUpper = xhrMethod.toUpperCase();
    const pathMap = { HEAD: 'get', OPTIONS: 'get', DELETE: 'delete', GET: 'get', POST: 'post', PUT: 'put', PATCH: 'patch' };
    const url = `https://httpbin.org/${pathMap[methodUpper] ?? 'post'}`;
    const hasBody = ['POST', 'PUT', 'PATCH'].includes(methodUpper);
    const payload = hasBody ? JSON.stringify({ singulr_xhr_test: { method: methodUpper, timestamp: new Date().toISOString() } }) : null;

    if (!xhrAsync) {
      const xhr = new XMLHttpRequest();
      try {
        xhr.open(methodUpper, url, false);
        if (hasBody) xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.send(payload);
        if (xhr.readyState !== XMLHttpRequest.DONE || xhr.status === 0) {
          showToast(`⚠ [SYNC→ASYNC HIJACK] ${methodUpper} — A browser extension intercepted this synchronous XHR and converted it to async. No synchronous response is available.`, 'warning');
          return;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          showToast(`[SYNC] ${methodUpper} → ${xhr.status} ${xhr.statusText}`, 'success');
        } else {
          throw new Error(`HTTP ${xhr.status} ${xhr.statusText}`);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        showToast(`[SYNC] ${methodUpper} → ${msg}`, 'error');
      }
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open(methodUpper, url, true);
    if (hasBody) xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.timeout = 10000;
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        showToast(`[ASYNC] ${methodUpper} → ${xhr.status} ${xhr.statusText}`, 'success');
      } else {
        showToast(`[ASYNC] ${methodUpper} → HTTP ${xhr.status} ${xhr.statusText}`, 'error');
      }
    };
    xhr.onerror = () => showToast(`[ASYNC] ${methodUpper} → Network error (no response received)`, 'error');
    xhr.ontimeout = () => showToast(`[ASYNC] ${methodUpper} → Request timed out after 10s`, 'error');
    xhr.send(payload);
  };

  const handleFetchClick = async () => {
    const methodUpper = fetchMethod.toUpperCase();
    const pathMap = { HEAD: 'get', OPTIONS: 'get', DELETE: 'delete', GET: 'get', POST: 'post', PUT: 'put', PATCH: 'patch' };
    const url = `https://httpbin.org/${pathMap[methodUpper] ?? 'post'}`;
    const hasBody = ['POST', 'PUT', 'PATCH'].includes(methodUpper);
    const body = hasBody ? JSON.stringify({ singulr_fetch_test: { method: methodUpper, timestamp: new Date().toISOString() } }) : undefined;

    const fetchIsNative = window.fetch.toString().includes('[native code]');
    if (!fetchIsNative) {
      showToast(`⚠ [FETCH PATCHED] window.fetch has been replaced by an extension or injected script. Requests may be intercepted, modified, or blocked.`, 'warning');
    }

    try {
      const response = await window.fetch(url, {
        method: methodUpper,
        headers: hasBody ? { 'Content-Type': 'application/json' } : undefined,
        body,
      });
      if (response.bodyUsed) {
        showToast(`⚠ [FETCH BODY CONSUMED] ${methodUpper} → response.bodyUsed is true before we read it. An interceptor has already consumed the response stream.`, 'warning');
        return;
      }
      if (response.redirected && !response.url.startsWith('https://httpbin.org')) {
        showToast(`⚠ [UNEXPECTED REDIRECT] ${methodUpper} → Response was redirected to ${response.url} by an extension or service worker.`, 'warning');
      }
      if (response.type === 'opaque' || response.type === 'error') {
        showToast(`⚠ [SERVICE WORKER / CORS] ${methodUpper} → Response type is '${response.type}'. A service worker or CORS policy blocked full access to the response.`, 'warning');
        return;
      }
      if (!response.ok) {
        showToast(`✕ [FETCH] ${methodUpper} → HTTP ${response.status} ${response.statusText} (fetch does not throw on HTTP errors — always check response.ok)`, 'error');
        return;
      }
      showToast(`✓ [FETCH] ${methodUpper} → ${response.status} ${response.statusText}`, 'success');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showToast(`✕ [FETCH] ${methodUpper} → Network error: ${msg}`, 'error');
    }
  };

  return (
    <div className="chat-page">
      <div className="chat-container" data-testid="chat-container">
        <header className="chat-header" data-testid="chat-header">
          <div className="header-content">
            <div className="header-left">
              <div className="icon-wrapper">
                <Sparkles className="sparkles-icon" />
              </div>
              <h1 className="header-title" data-testid="chat-title">
                AI Chat Assistant (React)
              </h1>
            </div>
            <div className="header-right">
              <select
                value={selectedEmail}
                onChange={(e) => handleEmailChange(e.target.value)}
                className="plan-selector"
                data-testid="email-selector"
              >
                <option value="">Not logged in</option>
                <option value="nishchay202@gmail.com">nishchay202@gmail.com</option>
                <option value="nishchay.rajput@singulr.ai">nishchay.rajput@singulr.ai</option>
              </select>
              <select
                value={selectedPlan}
                onChange={(e) => handlePlanChange(e.target.value)}
                className="plan-selector"
                data-testid="plan-selector"
                disabled={!selectedEmail}
              >
                <option value="">Select Plan</option>
                <option value="free">Free Plan</option>
                <option value="paid">Paid Plan</option>
                <option value="enterprise">Enterprise Plan</option>
              </select>
              <label
                className="disable-training-toggle"
                title="Disable training on data"
                data-testid="disable-training-toggle"
              >
                <input
                  type="checkbox"
                  checked={disableTrainingOnData}
                  onChange={(e) => handleDisableTrainingChange(e.target.checked)}
                  className="disable-training-checkbox"
                />
                No Training
              </label>
              <Button variant="outline" size="sm" className="new-chat-btn" data-testid="new-chat-button">
                + New Chat
              </Button>
              <a href="/multi" className="multi-input-link">
                Multi-Input →
              </a>
            </div>
          </div>
        </header>

        <ScrollArea
          className="messages-area"
          ref={scrollAreaRef}
          data-testid="messages-scroll-area"
        >
          <div className="messages-wrapper" data-testid="messages-container">
            {messages.length === 0 && (
              <div className="empty-state" data-testid="empty-state">
                <div className="empty-state-icon-wrapper">
                  <Sparkles className="empty-state-icon" />
                </div>
                <h2 className="empty-state-title">How can I help you today?</h2>
                <p className="empty-state-subtitle">Ask me anything or upload files to get started</p>
              </div>
            )}
            {messages.map((message) => (
              <Message key={message.id} message={message} />
            ))}
            {isLoading && <LoadingIndicator />}
          </div>
        </ScrollArea>

        <div className="input-section" data-testid="input-area">
          <div className="input-wrapper">
            {showConnectors && (
              <div className="connector-panel" data-testid="connectors-panel">
                <div className="connector-header">
                  <span className="connector-title">Select Connector</span>
                  <button onClick={() => setShowConnectors(false)} className="connector-close" data-testid="close-connectors-button">
                    <X className="close-icon" />
                  </button>
                </div>
                <div className="connector-grid">
                  {CONNECTORS.map((connector) => (
                    <button
                      key={connector.id}
                      onClick={() => {
                        setInputValue((prev) => `${prev}${prev ? ' ' : ''}@${connector.name} `);
                        setShowConnectors(false);
                        textareaRef.current?.focus();
                      }}
                      className="connector-button"
                      data-testid={`connector-${connector.id}`}
                    >
                      <span className="connector-icon">{connector.icon}</span>
                      <span className="connector-name">{connector.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div
              data-testid="input-container"
              className={`single-input-container${isDraggingInput ? ' area-card-dragging' : ''}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
            >
              {isDraggingInput && (
                <div className="area-drop-indicator" data-testid="drag-drop-overlay">
                  <Upload className="area-drop-icon" />
                  Drop to attach files
                </div>
              )}

              {attachments.length > 0 && (
                <div className="attachments-list" data-testid="attachment-preview-list">
                  {attachments.map((att) => (
                    <div className="attachment-preview" data-testid="attachment-preview" key={att.id}>
                      <Paperclip className="preview-icon" />
                      <span className="preview-name" data-testid="attachment-name">{att.name}</span>
                      <span className="preview-size" data-testid="attachment-size">({formatFileSize(att.size)})</span>
                      <button
                        onClick={() => setAttachments((prev) => prev.filter((a) => a.id !== att.id))}
                        className="preview-remove"
                        data-testid="remove-attachment-button"
                      >
                        <X className="remove-icon" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="input-container">
                <div className="textarea-wrapper">
                  <Textarea
                    ref={textareaRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    placeholder="Type your message... (Shift+Enter for new line)"
                    className="message-textarea"
                    data-testid="message-input"
                  />
                  <div className="input-actions">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="action-button"
                      onClick={() => setShowConnectors(!showConnectors)}
                      title="Add connector"
                      data-testid="toggle-connectors-button"
                    >
                      <Plus className="action-icon" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="action-button"
                      onClick={() => fileInputRef.current?.click()}
                      title="Attach file"
                      data-testid="attach-file-button"
                    >
                      <Paperclip className="action-icon" />
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleFileSelect}
                      data-testid="file-input"
                    />
                  </div>
                </div>
                <Button
                  onClick={handleSend}
                  disabled={!inputValue.trim() && attachments.length === 0}
                  size="icon"
                  className="send-button"
                  data-testid="send-button"
                >
                  <Send className="send-icon" />
                </Button>
              </div>
            </div>

            <div className="xhr-controls" data-testid="xhr-controls">
              <span className="xhr-section-label">XHR</span>
              <select
                value={xhrMethod}
                onChange={(e) => setXhrMethod(e.target.value)}
                className="xhr-method-select"
                data-testid="xhr-method-select"
              >
                {HTTP_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <button
                type="button"
                className={`xhr-mode-toggle ${xhrAsync ? 'xhr-mode-async' : 'xhr-mode-sync'}`}
                onClick={() => setXhrAsync((v) => !v)}
                data-testid="xhr-mode-toggle"
                title="Toggle XHR mode"
              >
                {xhrAsync ? 'Async' : 'Sync'}
              </button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="xhr-button"
                onClick={handleXhrClick}
                data-testid="xhr-request-button"
              >
                Send XHR
              </Button>

              <span className="xhr-section-label fetch-label">Fetch</span>
              <select
                value={fetchMethod}
                onChange={(e) => setFetchMethod(e.target.value)}
                className="fetch-method-select"
                data-testid="fetch-method-select"
              >
                {HTTP_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="fetch-button"
                onClick={handleFetchClick}
                data-testid="fetch-request-button"
              >
                Send Fetch
              </Button>
            </div>
          </div>
        </div>

        <Toast toast={toast} />
      </div>
    </div>
  );
};
